/**
 * Scored outcome instruments (`BACKLOG.md` M6): ODI, LEFS, DASH, and Neck Disability Index (NDI).
 * "Ship outcomes-ready first" per this module's own brief — the full 200+-instrument library and the
 * FOTO/QCDR registry integration that would consume these scores are both explicitly deferred.
 *
 * **This module stores and scores; it does not embed any instrument's actual items.** No question
 * text, no answer-option wording, no per-item scoring key for any of the four instruments below
 * appears anywhere in this codebase — a clinician (or a future intake form) enters the instrument's
 * own raw total, and this module only converts that already-summed raw score into a normalized
 * percent-disability figure using each instrument's published min/max range. That is a deliberate
 * scope boundary, not an oversight:
 *
 *   - **DASH is commercially licensed** — `BACKLOG.md` M7's own note is explicit that KOOS/HOOS and
 *     DASH require separate budget/negotiation and must not be assumed free. Embedding DASH's actual
 *     item content or its real scoring algorithm without a license would be a licensing violation,
 *     not just a citation gap, so this module structurally cannot do that: `rawScore` is a plain
 *     number the caller already computed elsewhere (by hand, from a licensed paper form, or from a
 *     properly licensed digital instrument this system does not provide).
 *   - **ODI, LEFS, and NDI's licensing status was not independently verified in this pass.** They are
 *     widely reported in clinical literature as free for clinical/research use, but that claim was
 *     not confirmed against each instrument's own current publisher/copyright holder here — treat
 *     "free to use" as unverified, not as settled, until someone does that check. The same
 *     store-a-score-only boundary applies to all three regardless: this module never renders or
 *     transcribes any instrument's actual item text.
 *
 * `min`/`max`/`higherIsWorse` below are each instrument's own published raw-score range and
 * direction (also not independently re-verified against a primary source this session — treat as a
 * reasonable starting structure, not as validated). A "percent disability" figure is a generically
 * useful summary regardless of small differences in exactly where each instrument's range sits, which
 * is why this module computes one rather than nothing — but the raw score itself, not this derived
 * percentage, is what should be treated as the record of truth and shown to a clinician who knows the
 * instrument.
 */

export const OUTCOME_INSTRUMENTS = ['ODI', 'LEFS', 'DASH', 'NDI'] as const;
export type OutcomeInstrument = (typeof OUTCOME_INSTRUMENTS)[number];

export function isOutcomeInstrument(value: string): value is OutcomeInstrument {
  return (OUTCOME_INSTRUMENTS as readonly string[]).includes(value);
}

export interface OutcomeInstrumentScale {
  readonly min: number;
  readonly max: number;
  /** `true` when a higher raw score means *more* disability/dysfunction (ODI, DASH, NDI); `false`
   * when a higher raw score means *better* function (LEFS is scored the opposite direction from the
   * other three — a real, easy-to-miss inversion this module's own scoring must not get backwards). */
  readonly higherIsWorse: boolean;
}

/** Not independently re-verified against each instrument's primary source this session — see this
 * module's own header. Reasonable starting ranges, not validated ones. */
export const OUTCOME_INSTRUMENT_SCALES: Readonly<
  Record<OutcomeInstrument, OutcomeInstrumentScale>
> = {
  ODI: { min: 0, max: 50, higherIsWorse: true },
  LEFS: { min: 0, max: 80, higherIsWorse: false },
  DASH: { min: 0, max: 100, higherIsWorse: true },
  NDI: { min: 0, max: 50, higherIsWorse: true },
};

export type PercentDisabilityResult =
  | { readonly ok: true; readonly percentDisability: number }
  | { readonly ok: false; readonly reason: 'raw_score_out_of_range' };

/**
 * Convert an already-summed raw score into a 0–100 percent-disability figure, honoring each
 * instrument's own `higherIsWorse` direction (LEFS is inverted relative to the other three — see
 * `OutcomeInstrumentScale`'s own comment). Refuses rather than clamping a score outside the
 * instrument's published range, matching this codebase's other billing/clinical pure functions'
 * "refuse rather than guess" convention: an out-of-range raw score is far more likely to be a
 * data-entry mistake (a transposed digit, the wrong instrument's total) than a genuinely valid
 * extreme value, and silently clamping it would hide that.
 */
export function toPercentDisability(
  instrument: OutcomeInstrument,
  rawScore: number,
): PercentDisabilityResult {
  const scale = OUTCOME_INSTRUMENT_SCALES[instrument];
  if (!Number.isFinite(rawScore) || rawScore < scale.min || rawScore > scale.max) {
    return { ok: false, reason: 'raw_score_out_of_range' };
  }
  const range = scale.max - scale.min;
  const normalized = range === 0 ? 0 : (rawScore - scale.min) / range;
  const percentDisability = scale.higherIsWorse ? normalized * 100 : (1 - normalized) * 100;
  return { ok: true, percentDisability: Math.round(percentDisability * 10) / 10 };
}
